import moment from 'moment'
import { Link } from 'react-router-dom'
import { ChatBubbleLeftRightIcon, EnvelopeIcon, PhoneIcon, UserIcon } from '@heroicons/react/20/solid'

const formatDate = value => {
	if (!value) return '—'
	const m = moment(value)
	return m.isValid() ? m.format('dddd, MMM D, YYYY') : '—'
}

const formatTimeRange = (start, end) => {
	const startMoment = start ? moment(start) : null
	const endMoment = end ? moment(end) : null
	if (!startMoment?.isValid()) return '—'
	const startLabel = startMoment.format('HH:mm')
	const endLabel = endMoment?.isValid() ? endMoment.format('HH:mm') : '—'
	return `${startLabel} – ${endLabel}`
}

const getCustomerId = booking => {
	if (!booking) return ''
	const raw = booking.customerId ?? booking.customer?.id ?? booking.customer?._id
	if (typeof raw === 'string' || typeof raw === 'number') return String(raw)
	if (raw && typeof raw === 'object') {
		return String(raw._id ?? raw.id ?? '')
	}
	return ''
}

const initialsFromName = name =>
	(name || '')
		.split(' ')
		.filter(Boolean)
		.map(part => part[0]?.toUpperCase())
		.slice(0, 2)
		.join('') || '??'

export default function BookingDetailsCard({ booking, onEdit, onDelete, deleting = false }) {
	const customerName =
		booking?.customerName ||
		booking?.customer?.name ||
		`${booking?.customer?.firstName || ''} ${booking?.customer?.lastName || ''}`.trim() ||
		'Unknown customer'

	const tel = (booking?.customerTel || booking?.customer?.tel || booking?.customer?.phone || '').trim()
	const customerId = getCustomerId(booking)
	const whatsappNumber = tel.replace(/\D/g, '')

	const title = booking?.title || booking?.service || 'Booking'
	const serviceName = booking?.service || booking?.resource?.name || booking?.resourceName || '—'
	const resourceName = booking?.resource?.name || booking?.resourceName || '—'
	const notes = booking?.notes?.trim()
	const formattedDate = formatDate(booking?.start)
	const formattedTimeRange = formatTimeRange(booking?.start, booking?.end)

	return (
		<div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
			<div className="border-b border-gray-200 px-6 py-5">
				<p className="text-sm font-medium uppercase tracking-wide text-gray-500">Booking details</p>
				<h1 className="mt-1 text-2xl font-semibold text-gray-900">{title}</h1>
				<p className="text-sm text-gray-500">{formattedDate}</p>
			</div>

			<div className="space-y-6 p-6">
				<div className="flex items-center gap-4">
					<div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-600 text-white">
						<span className="text-lg font-semibold">{initialsFromName(customerName)}</span>
					</div>
					<div>
						<p className="text-sm text-gray-500">Client</p>
						{customerId ? (
							<Link
								to={`/system/customers/details/${customerId}`}
								className="text-lg font-semibold text-indigo-700 hover:underline"
							>
								{customerName}
							</Link>
						) : (
							<span className="text-lg font-semibold text-gray-900">{customerName}</span>
						)}
					</div>
				</div>

				<dl className="grid gap-4 sm:grid-cols-2">
					<div>
						<dt className="text-sm text-gray-500">Service</dt>
						<dd className="text-base font-medium text-gray-900">{serviceName}</dd>
					</div>
					<div>
						<dt className="text-sm text-gray-500">Resource</dt>
						<dd className="text-base font-medium text-gray-900">{resourceName}</dd>
					</div>
					<div>
						<dt className="text-sm text-gray-500">Date</dt>
						<dd className="text-base font-medium text-gray-900">{formattedDate}</dd>
					</div>
					<div>
						<dt className="text-sm text-gray-500">Time</dt>
						<dd className="text-base font-medium text-gray-900">{formattedTimeRange}</dd>
					</div>
				</dl>

				{notes && (
					<div>
						<h3 className="text-sm font-medium text-gray-500">Notes</h3>
						<p className="mt-1 whitespace-pre-line text-sm text-gray-700">{notes}</p>
					</div>
				)}

				<div className="flex flex-col gap-3 sm:flex-row">
					<button
						type="button"
						onClick={onEdit}
						className="inline-flex flex-1 items-center justify-center rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-700">
							Edit booking
						</button>
					<button
						type="button"
						onClick={onDelete}
						disabled={deleting}
						className={`inline-flex flex-1 items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium shadow-sm transition ${
							deleting
								? 'cursor-not-allowed bg-gray-400 text-white'
								: 'bg-red-600 text-white hover:bg-red-700'
						}`}
					>
						{deleting ? 'Deleting…' : 'Delete booking'}
					</button>
				</div>

				<div className="grid gap-3 sm:grid-cols-3">
					{tel ? (
						<a
							href={`tel:${tel}`}
							className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 w-full"
						>
							<PhoneIcon className="h-5 w-5 text-gray-600" />
							Call client
						</a>
					) : (
						<div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-400 w-full">
							<PhoneIcon className="h-5 w-5" />
							Call client
						</div>
					)}

					{tel ? (
						<a
							href={`https://wa.me/${whatsappNumber}`}
							target="_blank"
							rel="noopener noreferrer"
							className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 w-full"
						>
							<ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-600" />
							WhatsApp
						</a>
					) : (
						<div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-400 w-full">
							<ChatBubbleLeftRightIcon className="h-5 w-5" />
							WhatsApp
						</div>
					)}

					{customerId ? (
						<Link
							to={`/system/customers/details/${customerId}`}
							className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 w-full"
						>
							<UserIcon className="h-5 w-5 text-gray-600" />
							Client profile
						</Link>
					) : (
						<div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-400 w-full">
							<UserIcon className="h-5 w-5" />
							Client profile
						</div>
					)}
				</div>
			</div>
		</div>
	)
}
